<script setup>
import { computed } from 'vue';

const props = defineProps({
  appointment: { type: Object, required: true },
  professionalColor: { type: String, default: '#3b82f6' },
  startHour: { type: Number, required: true },
  pixelsPerHour: { type: Number, default: 60 },
  lane: { type: Number, default: 0 },
  laneCount: { type: Number, default: 1 },
});
const emit = defineEmits(['open', 'dragStart']);

let didDrag = false;

const onMoveStart = e => {
  didDrag = false;
  const startY = e.clientY;
  const startX = e.clientX;
  const onMove = mv => {
    if (
      Math.abs(mv.clientY - startY) > 3 ||
      Math.abs(mv.clientX - startX) > 3
    ) {
      didDrag = true;
    }
  };
  const onUp = () => {
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  };
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
  emit('dragStart', {
    appointment: props.appointment,
    mode: 'move',
    event: e,
  });
};

const onOpen = () => {
  if (didDrag) {
    didDrag = false;
    return;
  }
  emit('open', props.appointment);
};
const onResizeStart = e => {
  e.stopPropagation();
  emit('dragStart', {
    appointment: props.appointment,
    mode: 'resize',
    event: e,
  });
};

const minutesFromStart = ts => {
  const d = new Date(ts);
  return d.getHours() * 60 + d.getMinutes() - props.startHour * 60;
};

const top = computed(
  () =>
    Math.max(0, minutesFromStart(props.appointment.starts_at)) *
    (props.pixelsPerHour / 60)
);
const height = computed(() => {
  const start = new Date(props.appointment.starts_at).getTime();
  const end = new Date(props.appointment.ends_at).getTime();
  return Math.max(20, ((end - start) / 60000) * (props.pixelsPerHour / 60));
});
const widthPct = computed(() => 100 / props.laneCount);
const leftPct = computed(() => widthPct.value * props.lane);

const isCancelled = computed(() => props.appointment.status === 'cancelled');
const isPending = computed(() => props.appointment.status === 'pending');
const isConfirmed = computed(() => props.appointment.status === 'confirmed');

const formatHour = ts =>
  new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const contactName = computed(
  () => props.appointment.contact?.name || props.appointment.title || '—'
);
</script>

<template>
  <button
    type="button"
    class="absolute overflow-hidden rounded-md text-left text-[11px] transition-shadow hover:shadow-md"
    :class="[
      isCancelled ? 'bg-n-slate-3 text-n-slate-9 line-through opacity-60' : '',
      isPending ? 'border border-l-[3px] bg-opacity-10' : '',
      isConfirmed ? 'text-white shadow-sm' : '',
    ]"
    :style="{
      top: `${top}px`,
      height: `${height}px`,
      left: `calc(${leftPct}% + 2px)`,
      width: `calc(${widthPct}% - 4px)`,
      ...(isPending
        ? {
            background: `${professionalColor}20`,
            borderColor: professionalColor,
            color: professionalColor,
          }
        : {}),
      ...(isConfirmed ? { background: professionalColor } : {}),
    }"
    @click="onOpen"
    @mousedown="onMoveStart"
  >
    <div class="px-2 py-1">
      <div class="truncate font-semibold">{{ contactName }}</div>
      <div class="truncate text-[10px] opacity-90">
        {{ formatHour(appointment.starts_at) }}
        &#x2013;
        {{ formatHour(appointment.ends_at) }}
      </div>
    </div>
    <div
      class="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize"
      @mousedown.stop="onResizeStart"
    />
  </button>
</template>
